import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { ThemeToggle } from "../components/ThemeToggle";

const SITE = "https://wørdle.nu";
const CANONICAL = `${SITE}/blog/makeupspejle-gillian-jones`;
const COOLSHOP_GILLIAN =
  "https://www.coolshop.dk/skoenhed/maerke%3Dgillian-jones/";

const articleLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline:
    "Pause i hverdagen: makeupspejle fra Gillian Jones og små ordgåder på Wørdle.nu",
  inLanguage: "da-DK",
  author: { "@type": "Organization", name: "Wørdle.nu" },
  publisher: {
    "@type": "Organization",
    name: "Wørdle.nu",
    url: SITE,
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": CANONICAL },
  url: CANONICAL,
};

/**
 * Dansk blogsides eneste artikel (ca. 800 ord). Ikke linket fra hovednavigationen.
 */
export function BlogGillianJonesPage() {
  return (
    <>
      <Helmet prioritizeSeoTags htmlAttributes={{ lang: "da" }}>
        <title>
          Makeupspejle fra Gillian Jones og hverdagsro med Wørdle.nu
        </title>
        <meta
          name="description"
          content="Læs om små pauser i hverdagen: praktiske makeupspejle fra Gillian Jones og hvordan daglige ordgåder på Wørdle.nu passer ind – uden at det føles som endnu en opgave."
        />
        <link rel="canonical" href={CANONICAL} />
        <link rel="alternate" hrefLang="da" href={CANONICAL} />
        <link rel="alternate" hrefLang="no" href={`${SITE}/no`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE}/`} />
        <meta property="og:title" content="Makeupspejle fra Gillian Jones og Wørdle.nu" />
        <meta
          property="og:description"
          content="Hverdag, spejl og ordgåder: hvordan små rutiner og Wørdle.nu kan give ro mellem aftaler og familieliv."
        />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="Wørdle.nu" />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      </Helmet>

      <div className="blog-page">
        <header className="blog-page-header">
          <Link to="/" className="blog-page-back">
            ← Forsiden
          </Link>
          <ThemeToggle locale="da" />
        </header>

        <article className="blog-article">
          <h1>
            Pause i hverdagen: makeupspejle fra Gillian Jones og små ordgåder
            på Wørdle.nu
          </h1>

          <p className="blog-lead">
            Når kalenderen er tætpakket, og badeværelset på samme tid fungerer
            som logistikcentral for hele familien, kan selv korte øjeblikke føles
            værdifulde. Et{" "}
            <a
              href={COOLSHOP_GILLIAN}
              target="_blank"
              rel="noopener noreferrer"
            >
              Gillian Jones spejl
            </a>{" "}
            med god belysning eller forstørrelse gør det lettere at få styr på
            detaljerne, før dagen for alvor tager fart – uden at du skal flytte
            halvdelen af hjemmet rundt for at finde det rigtige lys. Hos Coolshop
            finder du Gillian Jones under skønhed, med alt fra kompakte rejsemodeller
            til større bord- og vægspejle, der passer til både små lejligheder og
            mere permanente makeuphjørner.
          </p>

          <p>
            På{" "}
            <Link to="/">Wørdle.nu</Link> går tanken i en lidt anden retning,
            men den handler stadig om fokus og små, afgrænsede udfordringer. Du
            har seks forsøg til at gætte ét dansk ord på fem bogstaver – præcis
            som mange kender fra Wordle – og hvert forsøg giver feedback, så du
            gradvist snævrer mulighederne ind. Det er ikke et marathon; det er en
            mental strækøvelse, der sjældent varer mere end et par minutter, og
            som derfor kan ligge mellem morgenkaffe og første møde, eller når
            børnene endelig har fundet skoene.
          </p>

          <h2>Hvorfor små ritualer betyder noget</h2>
          <p>
            Vores hjerner elsker forudsigelige, overskuelige opgaver, når verden
            udenfor føles kaotisk. Et spejl, der står det rigtige sted, og hvor
            du ved, at lyset er ensartet, sparer ikke bare tid – det reducerer
            også den lille irritation, der opstår, når man skal presse sig ind
            på en halv kvadratmeter med tre andre stemmer i baggrunden.{" "}
            <strong>Makeupspejle fra Gillian Jones</strong> er netop tænkt som
            praktisk tilbehør: nogle modeller er oplagte til væg eller sugekop,
            andre er nemme at pakke ned, når weekendturen kalder. Det handler
            mindre om perfektion og mere om at have et fast punkt, hvor du kan
            samle dig selv, før næste punkt på listen.
          </p>

          <p>
            På samme måde er et ordspil som Wørdle en slags &quot;fast boks&quot;
            i dagen: reglerne ændrer sig ikke, brættet er altid fem gange seks
            felter, og du ved præcis, hvornår runden er slut – enten fordi du
            har løst opgaven, eller fordi de seks rækker er brugt op. Den form
            for tydelighed er beroligende, fordi den ikke kræver planlægning eller
            abonnement; du åbner siden, spiller, og går videre. For mange bliver
            det en vane, der ligner krydsordets plads i avisen, bare i en mere
            digital og visuel form.
          </p>

          <h2>Fra bogstaver til balance mellem pligter og fornøjelse</h2>
          <p>
            Når du gætter ord på dansk, træner du sproglig intuition: hvilke
            bogstavkombinationer findes, hvilke er sjældne, og hvordan bruger du
            den gule og grønne feedback klogt? Det er den samme type
            problemløsning, der gør det tilfredsstillende at få spejlet i den
            rigtige vinkel eller finde toilettasken, hvor alt har sin plads –
            små sejre, der ikke fylder i nyhedsflowet, men som stadig mærkes i
            kroppen. Hvis du vil udvide dit setup derhjemme, kan du med fordel
            se nærmere på udvalget af Gillian Jones-produkter på Coolshop, hvor
            du både finder spejle og opbevaring, der understøtter en mere rolig
            morgen – særligt i hjem, hvor flere generationer deler de samme
            kvadratmeter.
          </p>

          <p>
            Vi tror ikke på, at man skal &quot;nå det hele&quot; hver dag.
            Tværtimod: Wørdle.nu er skabt til at være let
            tilgængelig, uden login og uden at trække dig ind i endeløse
            notifikationer. Du bestemmer selv, om dagens ord bliver en del af
            morgenrutinen eller en sen aften, når computeren endelig er lukket.
            Kombineret med fysiske ritualer – et glas vand, lidt ro ved
            badeværelsesspejlet, eller fem minutter med telefonen i lommen –
            opstår der ofte en bedre følelse af kontrol, selv når ugen er travl.
          </p>

          <h2>Skønhed, funktion og respekt for din tid</h2>
          <p>
            Gillian Jones-brandet er for mange synonymt med stilrent design og
            hverdagsnytte: produkter, der ikke kun ser pæne ud på hylden, men som
            faktisk tåler at blive brugt, når hverdagen ikke ligner et
            reklamefoto. Uanset om du leder efter et spejl med LED-lys til
            makeupbordet eller en praktisk løsning til teenagerværelset, giver
            det mening at vælge kvalitet, der holder til gentagelse – præcis som
            det giver mening at have et ordspil, der loader hurtigt og ikke
            spilder din tid med unødige trin.
          </p>

          <p>
            Når du kombinerer sådanne fysiske valg med de små digitale pauser på
            Wørdle.nu, opstår der en slags bro mellem det
            konkrete hjem og det kognitive mini-projekt. Du behøver ikke vælge;
            de to ting udelukker ikke hinanden. Mange bruger faktisk et kort spil
            som mental opvarmning, før de går i gang med noget, der kræver
            koncentration – eller som afslutning, når hjernen har brug for at skifte
            gear efter arbejde eller studie.
          </p>

          <h2>Kort sagt</h2>
          <p>
            Hverdagen består af tusind små beslutninger. Nogle handler om, hvordan
            vi indretter os, så morgenen glider lettere – for eksempel med{" "}
            <strong>makeupspejle fra Gillian Jones</strong>, du kan finde samlet
            ét sted hos Coolshop. Andre handler om, hvordan vi holder sproget og
            nysgerrigheden i live med et enkelt ord ad gangen på Wørdle.nu.
            Uanset hvad din dag bringer, håber vi,
            du finder både et godt spejl, når du har brug for det, og et ord, der
            får dig til at smile, når de fem bogstaver endelig falder på plads.
          </p>
        </article>
      </div>
    </>
  );
}
